import React, { useState, useEffect } from 'react';
import { CModal, CModalBody, CButton, CModalTitle } from '@coreui/react';
import { toast } from 'react-toastify';
import { callTokenApi } from '../../../../apiCaller';
import CFormGroup from '../../../migration/CFormGroup';
import { CInputFile } from '../../../migration/CInput';
import CLabel from '../../../migration/CLabel';
import CIcon from '@coreui/icons-react';
import XLSX from 'xlsx';

const ModalImportCSV = ({ visible, onClose }) => {
     const [csvErrors, setCsvErrors] = useState([]);
     const [csvData, setCsvData] = useState([]);
     const [fileName, setFileName] = useState(null);
     const [isLoading, setIsLoading] = useState(false);

     useEffect(() => {
          if (!visible) {
               setCsvErrors([]);
               setCsvData([]);
               setFileName(null);
          }
     }, [visible]);

     const exportToFile = (type = 'csv', fileName = 'table-template') => {
          const dataToExport = [['accountId', 'visitorId', 'sessionId', 'metadata', 'b2c', 'b2b', 'email']];

          if (type === 'csv') {
               // https://reactjs.org/docs/code-splitting.html#import
               import('../../../../utils/index').then((utils) => {
                    // csv type will use this function to export correctly
                    utils.exportToCSV(`${fileName}.${type}`, dataToExport);
               });
          } else {
               var workSheet = XLSX.utils.aoa_to_sheet(dataToExport);

               // Add to workbook
               let workBook = XLSX.utils.book_new();
               XLSX.utils.book_append_sheet(workBook, workSheet, 'Sheet1');

               // If uses XLSX.writeFile to export csv file, open that file then Ctrl + S to save,
               // then close file without saving changes, open that file again all values
               // of each rows will merge into the first column of that row, so use another function to export csv file
               XLSX.writeFile(workBook, `${fileName}.${type}`);
          }
     };

     const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < line.length; i++) {
               const char = line[i];

               if (char === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                         current += '"';
                         i++;
                    } else {
                         inQuotes = !inQuotes;
                    }
               } else if (char === ',' && !inQuotes) {
                    result.push(current);
                    current = '';
               } else {
                    current += char;
               }
          }
          result.push(current);
          return result;
     };

     const handleFileSelect = (file) => {
          setFileName(file.name);
          const reader = new FileReader();

          reader.onload = (event) => {
               const text = event.target.result;
               const lines = text.trim().split('\n');
               const header = parseCSVLine(lines[0]).map((h) => h.trim());

               const expectedFields = ['accountId', 'visitorId', 'sessionId', 'metadata', 'b2c', 'b2b', 'email'];
               const missingHeaders = expectedFields.filter((field) => !header.includes(field));
               if (missingHeaders.length > 0) {
                    setCsvErrors([{ row: 1, message: `Missing columns: ${missingHeaders.join(', ')}` }]);
                    setCsvData([]);
                    return;
               }

               const data = [];
               const errors = [];

               for (let i = 1; i < lines.length; i++) {
                    let row = parseCSVLine(lines[i]).map((r) => r.trim());

                    if (row.length !== header.length) {
                         errors.push({ row: i + 1, message: 'Column count does not match header' });
                         continue;
                    }

                    const rowData = {};
                    header.forEach((h, index) => {
                         rowData[h] = row[index];
                    });

                    const rowErrors = [];
                    if (!rowData.accountId) rowErrors.push('AccountId is required');
                    if (rowData.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(rowData.email)) rowErrors.push('Email invalid');
                    if (rowData.metadata) {
                         try {
                              rowData.metadata = JSON.parse(rowData.metadata);
                         } catch (e) {
                              rowErrors.push('Invalid metadata JSON');
                         }
                    }

                    rowData.b2c = rowData.b2c === 'true';
                    rowData.b2b = rowData.b2b === 'true';

                    if (rowErrors.length > 0) {
                         errors.push({ row: i + 1, message: rowErrors.join(', ') });
                    } else {
                         data.push({
                              accountId: rowData.accountId,
                              visitorId: rowData.visitorId || null,
                              sessionId: rowData.sessionId || null,
                              metadata: rowData.metadata || null,
                              b2c: rowData.b2c,
                              b2b: rowData.b2b,
                              run: false,
                              email: rowData.email || null,
                         });
                    }
               }

               setCsvErrors(errors);
               setCsvData(data);
          };

          reader.readAsText(file);
     };

     const handleSubmit = () => {
          if (csvErrors.length > 0) {
               toast.error('Please fix CSV errors before submitting.');
               return;
          }
          if (csvData.length === 0) {
               toast.error('No valid data to submit.');
               return;
          }

          callTokenApi('admin/centralized-person/import', 'POST', { listPerson: csvData }).then((response) => {
               setIsLoading(true);
               if (response.status === 200) {
                    toast.success(response.data.message);
                    onClose();
                    setIsLoading(false);
               } else {
                    setIsLoading(false);
                    toast.error('Import Fail!');
               }
          });
     };

     return (
          <CModal
               visible={visible}
               backdrop='static'
               alignment='center'
               keyboard={false}
               portal={false}
               onClose={onClose}
               size='lg'
               className={`import-modal ${fileName ? 'pe-none' : ''}`}
          >
               <CModalBody>
                    <div className='import-modal-body'>
                         <CIcon name='cil-x' onClick={onClose} className='icon-close-popup' />
                         <CModalTitle>Import Table Values</CModalTitle>
                         <div className={`import-modal-step${fileName ? ' import-modal-step-disable' : ''}`}>
                              <p>Download my table's template:</p>
                              <CButton color='light' onClick={() => exportToFile('csv', 'table-template')} disabled={fileName}>
                                   DOWNLOAD
                              </CButton>
                         </div>
                         <CFormGroup className={`col-12${fileName ? ' import-modal-file-disable' : ''}`}>
                              <CInputFile
                                   custom
                                   id='custom-file-input'
                                   onChange={(e) => {
                                        if (e.target.files.length > 0) handleFileSelect(e.target.files[0]);
                                   }}
                                   disabled={isLoading}
                              />
                              <CLabel htmlFor='custom-file-input' variant='custom-file'>
                                   {fileName || 'Choose file...'}
                              </CLabel>
                         </CFormGroup>

                         {csvErrors.length > 0 && (
                              <div className='mt-3 text-danger import-modal-error'>
                                   <ul>
                                        {csvErrors.map((err, i) => (
                                             <li key={i}>
                                                  Row {err.row}: {err.message}
                                             </li>
                                        ))}
                                   </ul>
                              </div>
                         )}
                         {csvErrors.length === 0 && fileName && <p>{csvData.length} rows of data in your upload. </p>}

                         <div className='import-modal-button'>
                              <CButton color='primary' disabled={isLoading || csvErrors.length > 0 || csvData.length === 0} onClick={handleSubmit}>
                                   UPLOAD
                              </CButton>
                              <CButton color='light' type='button' onClick={onClose}>
                                   CANCEL
                              </CButton>
                         </div>
                    </div>
               </CModalBody>
          </CModal>
     );
};

export default ModalImportCSV;
